import { Button } from '@hologram-dimension/button';
import { DropdownPanel, type DropdownSection } from '@hologram-dimension/dropdown-panel';
import clsx from 'clsx/lite';
import ExportSimsModal from 'modals/ExportSims/ExportSims';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import useAnalytics from 'state/contexts/Analytics/useAnalytics';
import {
  EnumSimInventoryExportType,
  HologramSimIdsMap,
  type SimInventorySegmentName,
} from 'types/common';
import styles from './ExportSimsButton.module.css';

interface ExportSimsButtonProps {
  buttonText?: string;
  subtitle?: string;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  selectedSims?: HologramSimIdsMap;
  variant?: GetReactParameterBag<typeof Button>['variant'];
  placement?: GetReactParameterBag<typeof DropdownPanel>['placement'];
  segmentName: SimInventorySegmentName;
  id?: string;
}

const ExportSimsButton: FC<ExportSimsButtonProps> = ({
  buttonText = 'Export SIMs',
  subtitle,
  isLoading = false,
  disabled = false,
  className,
  selectedSims,
  variant = 'secondary',
  placement,
  segmentName,
  id,
}) => {
  const [internalShow, setShow] = useState(false);
  const [exportModalType, setShowExportModalType] = useState<EnumSimInventoryExportType | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const { sendAnalyticsEvent } = useAnalytics();

  const show = internalShow && !isLoading;

  const onClick = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  const exportSims = useCallback((exportType: EnumSimInventoryExportType) => {
    setShow(false);
    setShowExportModalType(exportType);
  }, []);

  const content = useMemo(() => {
    const newContent: Array<DropdownSection> = [];

    newContent.push({
      label: subtitle,
      items: [
        {
          children: 'Export to XLSX (Excel)',
          iconStart: 'DownloadTray',
          disabled,
          onClick: () => exportSims(EnumSimInventoryExportType.XLSX),
        },
        {
          children: 'Export to CSV',
          iconStart: 'DownloadTray',
          disabled,
          onClick: () => exportSims(EnumSimInventoryExportType.CSV),
        },
        {
          children: 'Export to JSON',
          iconStart: 'DownloadTray',
          disabled,
          onClick: () => exportSims(EnumSimInventoryExportType.JSON),
        },
      ],
    });

    return newContent;
  }, [disabled, exportSims, subtitle]);

  useEffect(() => {
    if (show && sendAnalyticsEvent) {
      sendAnalyticsEvent(`SIMs - ${segmentName} - Export SIMs`);
    }
  }, [segmentName, sendAnalyticsEvent, show]);

  return (
    <div className={clsx(styles.wrapper, className)} ref={containerRef} id={id}>
      <DropdownPanel
        className={styles.dropdownPanel}
        content={content}
        open={show}
        onOpenChange={setShow}
        placement={placement}
      >
        <Button
          iconEnd={show ? 'ChevronSingleNorth' : 'ChevronSingleSouth'}
          variant={variant}
          onClick={onClick}
          disabled={isLoading}
        >
          {buttonText}
        </Button>
      </DropdownPanel>
      {exportModalType !== null && selectedSims && (
        <ExportSimsModal
          onClose={() => {
            setShowExportModalType(null);
          }}
          sims={selectedSims}
          defaultOpen
          exportType={exportModalType}
          segmentName={segmentName}
        />
      )}
    </div>
  );
};

export default ExportSimsButton;
